import { QueryPersister } from '@tanstack/vue-query';

import { z } from 'zod';

import { getHash } from './getter';
import { SchemaType } from './types';

export type CacheData<T> = {
  data: T;
  expired: number;
};

export const localStoragePersiterHelper = (keyPrefix: string, Schema: z.ZodType, expired: number = 3600000) => {
  const getKey = (keySource: unknown[]) => {
    return keyPrefix + '_' + getHash(keySource.join(':'));
  };

  const persistHandler = (keySource: unknown[]) => (res: SchemaType<typeof Schema>) => {
    const cacheKey = getKey(keySource);
    const cacheData: CacheData<SchemaType<typeof Schema>> = {
      data: res,
      expired: Date.now() + expired,
    };
    window.localStorage.setItem(cacheKey, btoa(JSON.stringify(cacheData)));
    return res;
  };

  const persister: QueryPersister<SchemaType<typeof Schema>, string[]> = async (queryFn, context) => {
    const cacheKey = getKey(context.queryKey);
    const cacheDataStr = window.localStorage.getItem(cacheKey);

    if (cacheDataStr) {
      const cacheData: CacheData<unknown> = JSON.parse(atob(cacheDataStr));

      if (cacheData.expired && cacheData.expired > Date.now()) {
        const result = Schema.safeParse(cacheData.data);
        if (result.success) {
          await new Promise(resolve => setTimeout(resolve, 0));
          return result.data;
        }
      }

      window.localStorage.removeItem(cacheKey);
    }

    const result = await queryFn(context);
    return result;
  };

  return {
    persister,
    persistHandler,
  };
};
